import React, { useState } from "react";
import Modal from "./Modal";
import CardImage from "./CardImage";
import { toast } from "sonner";
import { Loader2, Sparkles, Save, Plus, X, Link2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const FIELDS = [
  { key: "name", label: "Full Name", placeholder: "Jane Doe", testid: "contact-name-field" },
  { key: "title", label: "Job Title", placeholder: "VP of Sales" },
  { key: "company", label: "Company", placeholder: "Acme Corp", testid: "contact-company-field" },
  { key: "industry", label: "Industry", placeholder: "Technology" },
  { key: "phone", label: "Phone", placeholder: "+1 555 123 4567" },
  { key: "phone2", label: "Phone (2nd)", placeholder: "+1 555 987 6543" },
  { key: "email", label: "Email", placeholder: "jane@acme.com" },
  { key: "email2", label: "Email (2nd)", placeholder: "jane@personal.com" },
  { key: "website", label: "Website", placeholder: "acme.com" },
  { key: "address", label: "Address", placeholder: "123 Market St" },
];

const inputClass =
  "w-full bg-[#0B0F17] border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors";

export default function CardEditor({ open, onClose, initial, aiExtracted, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(() => ({
    name: "", title: "", company: "", phone: "", phone2: "", email: "", email2: "",
    website: "", industry: "", address: "", notes: "", image_path: "",
    back_image_path: "", social_links: [], reminder_at: "", reminder_note: "",
    reminder_done: false, favorite: false, ...initial,
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setSocial = (i, v) =>
    setForm((f) => ({ ...f, social_links: f.social_links.map((s, idx) => (idx === i ? v : s)) }));
  const addSocial = () => setForm((f) => ({ ...f, social_links: [...(f.social_links || []), ""] }));
  const removeSocial = (i) =>
    setForm((f) => ({ ...f, social_links: f.social_links.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name && !form.company) {
      toast.error("Add at least a name or company");
      return;
    }
    const payload = { ...form, social_links: (form.social_links || []).map((s) => s.trim()).filter(Boolean) };
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/cards/${initial.id}`, payload);
        toast.success("Contact updated");
        onSaved(data);
      } else {
        const { data } = await api.post("/cards", payload);
        toast.success("Contact saved to your Rolodex");
        onSaved(data);
      }
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const hasImages = form.image_path || form.back_image_path;

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-2xl" testId="card-editor">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-1">
          {aiExtracted && (
            <span className="inline-flex items-center gap-1 text-xs bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full font-medium">
              <Sparkles className="w-3 h-3" /> AI extracted
            </span>
          )}
          {form.back_image_path && (
            <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full font-medium">
              Both sides merged
            </span>
          )}
        </div>
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">
          {isEdit ? "Edit contact" : "Review & save"}
        </h2>
        <p className="text-sm text-slate-400 mb-5">
          {aiExtracted ? "Check the details our AI read, tweak anything, then save." : "Enter the contact details."}
        </p>

        {hasImages && (
          <div className="mb-5 flex gap-3">
            {form.image_path && (
              <div className="flex-1 rounded-xl overflow-hidden border border-slate-800">
                <CardImage path={form.image_path} className="w-full h-28 object-cover" />
                <p className="text-[10px] text-center text-slate-500 py-1 bg-slate-900/80 font-mono uppercase tracking-wider">Front</p>
              </div>
            )}
            {form.back_image_path && (
              <div className="flex-1 rounded-xl overflow-hidden border border-slate-800">
                <CardImage path={form.back_image_path} className="w-full h-28 object-cover" />
                <p className="text-[10px] text-center text-slate-500 py-1 bg-slate-900/80 font-mono uppercase tracking-wider">Back</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === "address" ? "sm:col-span-2" : ""}>
              <label className="text-xs text-slate-400 mb-1 block">{f.label}</label>
              <input
                data-testid={f.testid || `field-${f.key}`}
                value={form[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
                placeholder={f.placeholder}
                className={inputClass}
              />
            </div>
          ))}

          <div className="sm:col-span-2">
            <label className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" /> Social & profile links
            </label>
            <div className="space-y-2">
              {(form.social_links || []).map((link, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    data-testid={`social-link-input-${i}`}
                    value={link}
                    onChange={(e) => setSocial(i, e.target.value)}
                    placeholder="https://linkedin.com/in/janedoe"
                    className={inputClass}
                  />
                  <button
                    onClick={() => removeSocial(i)}
                    className="shrink-0 w-10 rounded-lg border border-slate-700 text-slate-400 hover:text-red-400 hover:border-red-500/50 flex items-center justify-center transition-colors"
                    aria-label="Remove link"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                data-testid="add-social-link-button"
                onClick={addSocial}
                className="flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-400 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add link
              </button>
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-slate-400 mb-1 block">Notes (where you met, follow-ups…)</label>
            <textarea
              data-testid="field-notes"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              placeholder="Met at TechCrunch Disrupt, interested in Q3 pilot"
              className={inputClass}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="save-contact-button"
            onClick={save}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save contact</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}
